import { useMemo, useState } from 'react';
import { useApolloClient } from '@apollo/client/react';
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import { FieldValues, useController, useFormContext } from 'react-hook-form';

import {
    IVideoListItemFragment,
    VideoListItemFragmentDoc,
} from 'graphql/types';
import { AddButton } from 'common/components/Button/AddButton';
import {
    getVideoSelectStatus,
    isVideoAdded,
    isVideoSelected,
} from 'training/utils/videoSelect';
import { VideoListItem } from 'video/components/VideoListItem';
import { VideoSelectDrawer } from 'video/forms/Field/VideoSelectorField/VideoSelectDrawer';
import { TControllerProps } from 'common/types';
import { asTypedControl } from 'common/utils/form';

interface IProps {
    name: string;
}

export const VideoSelectorField = <TFieldValues extends FieldValues>({
    control: formControl,
    name,
}: IProps & TControllerProps<TFieldValues>) => {
    const [translate] = useTranslation();
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedVideos, setSelectedVideos] = useState<
        IVideoListItemFragment[]
    >([]);
    const client = useApolloClient();

    const control = asTypedControl<string[]>(name, formControl);

    const { setValue } = useFormContext();
    const {
        field: { value: addedVideoIds },
        fieldState: { error, isTouched },
    } = useController({
        name,
        control,
    });

    const addedVideos = useMemo(() => {
        // Get video objects from cache based on the video ids set in the form
        const videos =
            addedVideoIds
                ?.map((addedVideoId) => {
                    try {
                        return client.cache.readFragment<IVideoListItemFragment>(
                            {
                                id: `Video:${addedVideoId}`,
                                fragment: VideoListItemFragmentDoc,
                                fragmentName: 'VideoListItem',
                            }
                        );
                    } catch {
                        return null;
                    }
                })
                .filter(Boolean) || [];

        return videos as IVideoListItemFragment[];
    }, [addedVideoIds, client.cache]);

    const handleDrawerOpen = (open: boolean) => {
        if (!open) setSelectedVideos([]);

        setDrawerOpen(open);
    };

    const handleSelectVideo = (video: IVideoListItemFragment) => {
        const newVideos = [...selectedVideos, video].sort((a, b) =>
            a.title > b.title ? 1 : -1
        );

        setSelectedVideos(newVideos);
    };

    const handleRemoveVideo = (video: IVideoListItemFragment) => {
        const { isSelected } = getVideoSelectStatus(
            selectedVideos,
            addedVideos,
            video
        );

        if (isSelected) {
            const newSelectedVideos = selectedVideos.filter(
                (selectedVideo) => selectedVideo.id !== video.id
            );

            setSelectedVideos(newSelectedVideos);

            return;
        }

        setValue(
            name,
            addedVideos.filter((addedVideo) => addedVideo.id !== video.id),
            { shouldDirty: true }
        );
    };

    const handleAddVideos = () => {
        setValue(
            name,
            [
                ...(addedVideoIds || []),
                ...selectedVideos.map((selectedVideo) => selectedVideo.id),
            ],
            { shouldDirty: true }
        );

        setSelectedVideos([]);

        handleDrawerOpen(false);
    };

    return (
        <Box>
            <Box
                sx={{
                    alignItems: 'left',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <AddButton
                    aria-label={translate('addVideosTitle')}
                    error={
                        !!error?.message && isTouched
                            ? error.message
                            : undefined
                    }
                    onClick={() => handleDrawerOpen(true)}
                >
                    {translate('addVideosTitle')}
                </AddButton>

                {!!addedVideos.length && (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            mt: 2,
                        }}
                    >
                        {addedVideos.map((addedVideo) => (
                            <VideoListItem
                                addButton
                                added={isVideoAdded(addedVideos, addedVideo)}
                                key={`added-${addedVideo.id}`}
                                selected={isVideoSelected(
                                    selectedVideos,
                                    addedVideo
                                )}
                                video={addedVideo}
                                onClick={handleRemoveVideo}
                            />
                        ))}
                    </Box>
                )}
            </Box>

            <VideoSelectDrawer
                addedVideos={addedVideos}
                open={drawerOpen}
                selectedVideos={selectedVideos}
                onAddVideos={handleAddVideos}
                onDrawerOpen={handleDrawerOpen}
                onRemoveVideo={handleRemoveVideo}
                onSelectVideo={handleSelectVideo}
            />
        </Box>
    );
};
